import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextInvoice } from './Invoice';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { InvoiceDA } from '../../../../services/admin/invoice.service';
import { PaymentDA } from '../../../../services/admin/payment.service';
import { Error } from '../../../../models/error.model';
import { Invoice } from '../../../../models/invoice.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { Reminder } from '../../../../models/reminder.model';
import { modalShow } from '../../../../utils/modal.util';
import { formatMoney } from '../../../../scripts/format.value.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { colorStatusInvoice } from '../../../../scripts/badge.color.script';
import { treasuryAccountSelected } from '../../../../scripts/list.selected.script';
import { normalizeStatus, statusDocumentValue } from '../../../../libraries/status.document.library';
import { paymentMethodValue } from '../../../../libraries/payment.library';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { normalizeContactData } from '../../../../scripts/format.value.script';
import { MediaDA } from '../../../../services/admin/media.service';

import AppSaleViewDetail from '../component/SaleViewDetail';
import AppSaleViewRequirements from '../component/SaleViewRequirements';
import AppSaleViewAmount from '../component/SaleViewAmount';
import AppSaleViewSignatures from '../component/SaleViewSignatures';
import AppPurchaseViewDraft from '../../purchase/component/PurchaseViewDraft';
import AppModalInvoiceCreateReminder from '../../../../components/modal/ModalInvoiceCreateReminder';
import AppModalInvoiceUpdateReminder from '../../../../components/modal/ModalInvoiceUpdateReminder';
import AppModalInvoiceUpdateValidate from '../../../../components/modal/ModalInvoiceUpdateStatusValidate';
import AppModalInvoiceUpdateInvalidate from '../../../../components/modal/ModalInvoiceUpdateStatusInvalidate';
import AppModalPaymentCreateInvoice from '../../../../components/modal/ModalPaymentCreateInvoice';
import AppPurchaseViewVouchers from '../../purchase/component/PurchaseViewVouchers';
import AppPreloader from '../../../../components/Preloader';
import AppModalSendEmail from '../../../../components/modal/ModalSendEmail';
import AppModalWriteOffInvoice from '../../../../components/modal/ModalWriteOffInvoice';
import DocumentNavigation from '../../../../components/element/DocumentNavigation';

export interface InvoiceViewPageProps {};

let errorResponse: Error, invoiceResponse: Invoice, paymentResponse: Invoice, reminderResponse: Invoice;

const InvoiceViewPage: React.FunctionComponent<InvoiceViewPageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, accountingAccountsForCompany, accountingCreditEntriesForCompany, treasuryAccountsForCompany, servicesForCompany, productsForCompany, accountingIncomeAccountsForCompany, loadAccountingCreditEntriesForCompany} = UseOutletContextInvoice()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [removePayment, setRemovePayment] = useState(false)
  const [reminder, setReminder] = useState<Reminder | null>(null)
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const setInvoiceNormalized: Dispatch<SetStateAction<Invoice | null>> = (value) => {
    setInvoice((prev) => {
      const next = typeof value === 'function' ? (value as (p: Invoice | null) => Invoice | null)(prev) : value
      if (!next) return next
      return {
        ...next,
        status: normalizeStatus(next.status),
        ...(next.payment_status ? { payment_status: normalizeStatus(next.payment_status) } : {}),
      }
    })
  }
  const [vouchers, setVouchers] = useState({list: true, value: [] as {name: string, size: number, type: string, view: string, file: any}[], valid: [] as {name: boolean, file: boolean}[]})

  const loadInvoice = async (id_company: string, id_invoice: string) => {
    await InvoiceDA.getInvoice(id_company, id_invoice).then( async (response) => {
      if (response.status === 200) {
        invoiceResponse = response.data
        invoiceResponse.status = normalizeStatus(invoiceResponse.status)
        if (invoiceResponse.payment_status) invoiceResponse.payment_status = normalizeStatus(invoiceResponse.payment_status)
        normalizeContactData(invoiceResponse.customer)

        let tempVouchersValue: {name: string, size: number, type: string, view: string, file: any}[] = []
        let tempVouchersValid: {name: boolean, file: boolean}[] = []

        for (let voucher of (invoiceResponse.vouchers ?? [])) {
          const vName = voucher.original_name ?? voucher.name ?? ''
          const vType = voucher.content_type ?? 'application/pdf'
          let vView = ''

          if (voucher.id_document_inbox && companyForUser) {
            const signedUrl = await MediaDA.getDocumentSignedUrl(companyForUser.id, voucher.id_document_inbox)
            if (signedUrl) vView = signedUrl
          } else if (voucher.file) {
            vView = voucher.file
          }

          tempVouchersValue.push({
            name: vName,
            size: voucher.size ?? 0,
            type: vType,
            view: vView,
            file: vView
          })
          tempVouchersValid.push({ name: !!vName, file: !!vView })
        }

        setVouchers({list: true, value: tempVouchersValue, valid: tempVouchersValid})
        setInvoice(invoiceResponse)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const submitPaymentDelete = async (item: string) => {
    if (companyForUser && invoice) {
      GlobalSwal.fire({
        title: lang.labels.youWantToDeleteThisPayment,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          let companyData = {subject_tax: settingsForCompany?.subject_tax ?? companyForUser?.subject_tax ?? false, tax_method: settingsForCompany?.tax_method ?? companyForUser?.tax_method ?? 'effective', tax_declaration: settingsForCompany?.tax_declaration ?? companyForUser?.tax_declaration ?? 'invoicing', tax_period: settingsForCompany?.tax_period ?? companyForUser?.tax_period ?? '', tax_statement: settingsForCompany?.tax_statement ?? companyForUser?.tax_statement ?? '', language: settingsForCompany?.language ?? companyForUser?.language ?? 'FR'}

          await PaymentDA.deletePaymentInvoice(companyForUser.id, invoice.id, item, companyData).then( (response) => {
            if (response.status === 200) {
              paymentResponse = response.data

              setInvoice(paymentResponse)
              loadAccountingCreditEntriesForCompany(companyForUser.id)

              GlobalSwal.fire({
                title: lang.labels.successfullyDeletedPayment,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitReminderDelete = async (item: string) => {
    if (companyForUser && invoice) {
      GlobalSwal.fire({
        title: lang.labels.youWantToDeleteThisReminder,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await InvoiceDA.deleteInvoiceReminder(companyForUser.id, invoice.id, item).then( (response) => {
            if (response.status === 200) {
              reminderResponse = response.data
              setInvoice(reminderResponse)

              GlobalSwal.fire({
                title: lang.labels.successfullyDeletedReminder,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitInvoiceRevertToDraft = async () => {
    if (companyForUser && invoice) {
      GlobalSwal.fire({
        title: lang.labels.youWantToRevertThisDocumentToDraft,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesConfirm,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await InvoiceDA.revertToDraft(companyForUser.id, invoice.id).then((response) => {
            if (response.status === 200) {
              setInvoice(response.data)

              GlobalSwal.fire({
                title: lang.labels.successfullyUpdated,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch((error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const submitInvoiceDelete = async () => {
    if (companyForUser && invoice) {
      GlobalSwal.fire({
        title: lang.labels.youWantToDeleteThisDocument,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await InvoiceDA.deleteInvoice(companyForUser.id, invoice.id).then((response) => {
            if (response.status === 200) {
              GlobalSwal.fire({
                title: lang.labels.successfullyDeletedDocument,
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then(() => {
                navigate('/main/sale/invoice/list', {replace: true})
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch((error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const excuteViewOrHideButtonsToPaymentDelete = () => {
    setRemovePayment(!removePayment)
  }

  const executeShowModalInvoiceUpdateValidateInvalidate = () => {
    if (companyForUser && invoice) {
      const subjectTax = settingsForCompany?.subject_tax ?? companyForUser?.subject_tax ?? false
      if (!subjectTax && (invoice.requirements ?? []).some((item) => ((item.contents ?? []).some((sub_item) => { const taxVal = sub_item.tax_value ?? sub_item.tax_amount ?? 0; return sub_item.tax_rate > 0 || taxVal !== 0 })))) {
        GlobalSwal.fire({
          title: lang.labels.servicesProductsWithTaxGreaterThanZero,
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      } else {
        modalShow(`modal-invoice-update-${(invoice.validated) ? "in" : ""}validate`)
      }
    }
  }

  const executeShowModalPaymentCreateInvoice = () => {
    modalShow('modal-payment-create-invoice')
  }

  const executeShowModalWriteOff = () => {
    modalShow('modal-write-off-invoice')
  }

  const submitWriteOffDelete = async (id_write_off: string) => {
    if (!companyForUser || !invoice) return
    const result = await GlobalSwal.fire({
      title: lang.labels.writeOffCancelConfirm,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm,
      cancelButtonText: lang.labels.noCancel
    })
    if (!result.isConfirmed) return
    const response = await InvoiceDA.deleteWriteOff(companyForUser.id, invoice.id, id_write_off)
    if (response.status === 200) {
      const updated = response.data
      updated.status = normalizeStatus(updated.status)
      if (updated.payment_status) updated.payment_status = normalizeStatus(updated.payment_status)
      normalizeContactData(updated.customer)
      setInvoice(updated)
      GlobalSwal.fire({ title: lang.labels.writeOffCancelSuccess, icon: 'success', showConfirmButton: false, timer: 1500 })
    } else {
      const errData = response.data
      GlobalSwal.fire({
        title: getError(errData?.code, lang.code, errData?.message),
        text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
        icon: 'error',
        confirmButtonText: lang.labels.okGotIt
      })
    }
  }

  const executeShowModalSendEmail = () => {
    modalShow('modal-send-email')
  }

  const executeShowModalCorrectAccounts = () => {
    modalShow('modal-invoice-correct-accounts')
  }

  const executeShowModalInvoiceCreateReminder = () => {
    modalShow('modal-invoice-create-reminder')
  }

  const executeShowModalInvoiceUpdateReminder = (item: Reminder) => {
    setReminder(item)
    modalShow('modal-invoice-update-reminder')
  }

  const executeSuspendDunning = async () => {
    if (!companyForUser || !invoice) return
    const result = await GlobalSwal.fire({
      title: lang.labels.suspendDunning,
      text: lang.labels.dunningWillPause,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm,
      cancelButtonText: lang.labels.noCancel
    })
    if (!result.isConfirmed) return
    const response = await InvoiceDA.suspendDunning(companyForUser.id, invoice.id)
    if (response.status === 200) {
      setInvoice(response.data)
      GlobalSwal.fire({ title: lang.labels.dunningSuspended, icon: 'success', showConfirmButton: false, timer: 1500 })
    } else {
      GlobalSwal.fire({ title: getError(response.data?.code, lang.code, response.data?.message), icon: 'error', confirmButtonText: lang.labels.okGotIt })
    }
  }

  const executeResumeDunning = async () => {
    if (!companyForUser || !invoice) return
    const response = await InvoiceDA.resumeDunning(companyForUser.id, invoice.id)
    if (response.status === 200) {
      setInvoice(response.data)
      GlobalSwal.fire({ title: lang.labels.dunningResumed, icon: 'success', showConfirmButton: false, timer: 1500 })
    } else {
      GlobalSwal.fire({ title: getError(response.data?.code, lang.code, response.data?.message), icon: 'error', confirmButtonText: lang.labels.okGotIt })
    }
  }

  const executeStopDunning = async () => {
    if (!companyForUser || !invoice) return
    const result = await GlobalSwal.fire({
      title: lang.labels.stopDunning,
      text: lang.labels.dunningStopIrreversible,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesStop,
      cancelButtonText: lang.labels.noCancel
    })
    if (!result.isConfirmed) return
    const response = await InvoiceDA.stopDunning(companyForUser.id, invoice.id)
    if (response.status === 200) {
      setInvoice(response.data)
      GlobalSwal.fire({ title: lang.labels.dunningStopped, icon: 'success', showConfirmButton: false, timer: 1500 })
    } else {
      GlobalSwal.fire({ title: getError(response.data?.code, lang.code, response.data?.message), icon: 'error', confirmButtonText: lang.labels.okGotIt })
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadInvoice(companyForUser.id, param.id)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.invoices, branch: lang.labels.viewInvoice}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && settingsForCompany && treasuryAccountsForCompany && servicesForCompany && productsForCompany && invoice
        ?
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-fluid order-2 order-lg-1 mb-5 mb-lg-0 me-lg-5">
          <div className="card card-flush mb-5">
            <div className="card-header pt-3">
              <div className="card-title align-items-start flex-column">
                <div className="d-flex align-items-center gap-3">
                  <h1 className="mb-0">{invoice.invoice_number}</h1>
                  {companyForUser && param.id && <DocumentNavigation navKey="nav_invoices" currentId={param.id} companyId={companyForUser.id} basePath="/main/sale/invoice/view/" />}
                </div>
                <label className="fs-6 fw-bold mt-1">
                  {lang.labels.status}:
                  <span className={`badge badge-light-${colorStatusInvoice(invoice.status)} text-uppercase fs-7 ms-2`}>{statusDocumentValue(lang, invoice.status)}</span>
                </label>
                { (invoice as any).remote_decision && (invoice as any).remote_decision !== 'none' &&
                  <label className="fs-6 fw-bold mt-1">
                    {lang.labels.recipientDecision}:
                    <span className={`badge badge-light-${(invoice as any).remote_decision === 'accepted' ? 'success' : 'danger'} text-uppercase fs-7 ms-2`}>
                      {(invoice as any).remote_decision === 'accepted' ? lang.labels.decisionAccepted : lang.labels.decisionDeclined}
                    </span>
                    { (invoice as any).remote_decided_at &&
                      <span className="text-muted fs-7 ms-2">{dateFormat(lang.code, (invoice as any).remote_decided_at)}</span>
                    }
                  </label>
                }
              </div>
              <div className="card-toolbar">
                { invoice.status !== "completed" && invoice.status !== "charged-off" &&
                  <>
                    { invoice.payment_status !== "fully-paid" &&
                      <button className={`btn ${invoice.validated ? "btn-light-primary" : "btn-primary"} mb-1 me-2`} type="button" onClick={executeShowModalInvoiceUpdateValidateInvalidate}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M17.5 11H6.5C4 11 2 9 2 6.5C2 4 4 2 6.5 2H17.5C20 2 22 4 22 6.5C22 9 20 11 17.5 11ZM15 6.5C15 7.9 16.1 9 17.5 9C18.9 9 20 7.9 20 6.5C20 5.1 18.9 4 17.5 4C16.1 4 15 5.1 15 6.5Z" fill="black"></path>
                              <path opacity="0.3" d="M17.5 22H6.5C4 22 2 20 2 17.5C2 15 4 13 6.5 13H17.5C20 13 22 15 22 17.5C22 20 20 22 17.5 22ZM4 17.5C4 18.9 5.1 20 6.5 20C7.9 20 9 18.9 9 17.5C9 16.1 7.9 15 6.5 15C5.1 15 4 16.1 4 17.5Z" fill="black"></path>
                            </svg>
                          </span>
                          {invoice.validated ? lang.labels.invalidate : lang.labels.validate}
                        </div>
                      </button>
                    }
                  </>
                }
                { invoice.validated
                  ?
                  <>
                    <button className="btn btn-light-primary mb-1 me-2" type="button" onClick={executeShowModalCorrectAccounts}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M2 4.63158C2 3.1782 3.1782 2 4.63158 2H13.47C14.0155 2 14.278 2.66919 13.8778 3.04006L12.4556 4.35821C11.9009 4.87228 11.1726 5.15789 10.4163 5.15789H7.1579C6.05333 5.15789 5.15789 6.05333 5.15789 7.1579V16.8421C5.15789 17.9467 6.05333 18.8421 7.1579 18.8421H16.8421C17.9467 18.8421 18.8421 17.9467 18.8421 16.8421V13.7518C18.8421 12.927 19.1817 12.1387 19.7809 11.572L20.9878 10.4308C21.3703 10.0691 22 10.3403 22 10.8668V19.3684C22 20.8218 20.8218 22 19.3684 22H4.63158C3.1782 22 2 20.8218 2 19.3684V4.63158Z" fill="black"/>
                            <path d="M10.9256 11.1882C10.5351 10.7977 10.5351 10.1645 10.9256 9.77397L18.0669 2.6327C18.8479 1.85165 20.1143 1.85165 20.8953 2.6327L21.3665 3.10391C22.1476 3.88496 22.1476 5.15129 21.3665 5.93234L14.2252 13.0736C13.8347 13.4641 13.2016 13.4641 12.811 13.0736L10.9256 11.1882Z" fill="black"/>
                            <path d="M8.82343 12.0064L8.08852 14.3348C7.8655 15.0414 8.46151 15.7366 9.19388 15.6242L11.8974 15.2092C12.4642 15.1222 12.6916 14.4278 12.2861 14.0223L9.98595 11.7221C9.61452 11.3507 8.98154 11.5055 8.82343 12.0064Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.correctAccounts}
                      </div>
                    </button>
                    { (invoice.reminders ?? []).length === 0 &&
                      <button className="btn btn-primary mb-1 me-2" type="button" onClick={executeShowModalInvoiceCreateReminder}>
                        <div className="d-flex align-items-center">
                          <span className="svg-icon svg-icon-2 ms-0">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path opacity="0.3" d="M14 3V20H2V3C2 2.4 2.4 2 3 2H13C13.6 2 14 2.4 14 3ZM11 13V11C11 9.7 10.2 8.59995 9 8.19995V7C9 6.4 8.6 6 8 6C7.4 6 7 6.4 7 7V8.19995C5.8 8.59995 5 9.7 5 11V13C5 13.6 4.6 14 4 14V15C4 15.6 4.4 16 5 16H11C11.6 16 12 15.6 12 15V14C11.4 14 11 13.6 11 13Z" fill="black"/>
                              <path d="M2 20H14V21C14 21.6 13.6 22 13 22H3C2.4 22 2 21.6 2 21V20ZM9 3V2H7V3C7 3.6 7.4 4 8 4C8.6 4 9 3.6 9 3ZM6.5 16C6.5 16.8 7.2 17.5 8 17.5C8.8 17.5 9.5 16.8 9.5 16H6.5ZM21.7 12C21.7 11.4 21.3 11 20.7 11H17.6C17 11 16.6 11.4 16.6 12C16.6 12.6 17 13 17.6 13H20.7C21.2 13 21.7 12.6 21.7 12ZM17 8C16.6 8 16.2 7.80002 16.1 7.40002C15.9 6.90002 16.1 6.29998 16.6 6.09998L19.1 5C19.6 4.8 20.2 5 20.4 5.5C20.6 6 20.4 6.60005 19.9 6.80005L17.4 7.90002C17.3 8.00002 17.1 8 17 8ZM19.5 19.1C19.4 19.1 19.2 19.1 19.1 19L16.6 17.9C16.1 17.7 15.9 17.1 16.1 16.6C16.3 16.1 16.9 15.9 17.4 16.1L19.9 17.2C20.4 17.4 20.6 18 20.4 18.5C20.2 18.9 19.9 19.1 19.5 19.1Z" fill="black"/>
                            </svg>
                          </span>
                          {lang.labels.createReminder}
                        </div>
                      </button>
                    }
                  </>
                  :
                  <Link to={"/main/sale/invoice/update/" + invoice.id} className="btn btn-primary mb-1 me-2">
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M2 4.63158C2 3.1782 3.1782 2 4.63158 2H13.47C14.0155 2 14.278 2.66919 13.8778 3.04006L12.4556 4.35821C11.9009 4.87228 11.1726 5.15789 10.4163 5.15789H7.1579C6.05333 5.15789 5.15789 6.05333 5.15789 7.1579V16.8421C5.15789 17.9467 6.05333 18.8421 7.1579 18.8421H16.8421C17.9467 18.8421 18.8421 17.9467 18.8421 16.8421V13.7518C18.8421 12.927 19.1817 12.1387 19.7809 11.572L20.9878 10.4308C21.3703 10.0691 22 10.3403 22 10.8668V19.3684C22 20.8218 20.8218 22 19.3684 22H4.63158C3.1782 22 2 20.8218 2 19.3684V4.63158Z" fill="black"/>
                          <path d="M10.9256 11.1882C10.5351 10.7977 10.5351 10.1645 10.9256 9.77397L18.0669 2.6327C18.8479 1.85165 20.1143 1.85165 20.8953 2.6327L21.3665 3.10391C22.1476 3.88496 22.1476 5.15129 21.3665 5.93234L14.2252 13.0736C13.8347 13.4641 13.2016 13.4641 12.811 13.0736L10.9256 11.1882Z" fill="black"/>
                          <path d="M8.82343 12.0064L8.08852 14.3348C7.8655 15.0414 8.46151 15.7366 9.19388 15.6242L11.8974 15.2092C12.4642 15.1222 12.6916 14.4278 12.2861 14.0223L9.98595 11.7221C9.61452 11.3507 8.98154 11.5055 8.82343 12.0064Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.edit}
                    </div>
                  </Link>
                }
                <button className="btn btn-primary mb-1 me-2" type="button" onClick={executeShowModalSendEmail}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.43 8.56949L10.744 15.1395C10.6422 15.282 10.5804 15.4492 10.5651 15.6236C10.5498 15.7981 10.5815 15.9734 10.657 16.1315L13.194 21.4425C13.2737 21.6097 13.3991 21.751 13.5557 21.8499C13.7123 21.9488 13.8938 22.0014 14.079 22.0015H14.117C14.3087 21.9941 14.4941 21.9307 14.6502 21.8191C14.8062 21.7075 14.9261 21.5526 14.995 21.3735L21.933 3.33649C22.0011 3.15918 22.0164 2.96594 21.977 2.78013C21.9376 2.59432 21.8452 2.4239 21.711 2.28949L15.43 8.56949Z" fill="black"/>
                        <path opacity="0.3" d="M20.664 2.06648L2.62602 9.00148C2.44768 9.07085 2.29348 9.19082 2.1824 9.34663C2.07131 9.50244 2.00818 9.68731 2.00074 9.87853C1.99331 10.0697 2.04189 10.259 2.14054 10.4229C2.23919 10.5869 2.38359 10.7185 2.55601 10.8015L7.86601 13.3365C8.02383 13.4126 8.19925 13.4448 8.37382 13.4297C8.54839 13.4145 8.71565 13.3526 8.85801 13.2505L15.43 8.56548L21.711 2.28448C21.5762 2.15096 21.4055 2.05932 21.2198 2.02064C21.034 1.98196 20.8409 1.99788 20.664 2.06648Z" fill="black"/>
                      </svg>
                    </span>
                    {lang.labels.sendByEmail}
                  </div>
                </button>
                <Link to={"/main/sale/invoice/create/reference/" + invoice.id} className="btn btn-primary mb-1 me-2">
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.5" x="7" y="2" width="14" height="16" rx="3" fill="black"/>
                        <rect x="3" y="6" width="14" height="16" rx="3" fill="black"/>
                      </svg>
                    </span>
                    {lang.labels.makeCopy}
                  </div>
                </Link>
                { !invoice.validated && invoice.status === 'in-progress' &&
                  <button className="btn btn-light-warning mb-1 me-2" type="button" onClick={submitInvoiceRevertToDraft}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M21 22H3C2.4 22 2 21.6 2 21V5C2 4.4 2.4 4 3 4H21C21.6 4 22 4.4 22 5V21C22 21.6 21.6 22 21 22Z" fill="black"/>
                          <path d="M12 12.4L7 17H17L12 12.4Z" fill="black"/>
                          <path opacity="0.3" d="M12 6V12.4L7 17H3V5C3 4.4 3.4 4 4 4H20C20.6 4 21 4.4 21 5V17H17L12 12.4V6Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.revertToDraft}
                    </div>
                  </button>
                }
                { invoice.status === 'draft' &&
                  <button className="btn btn-danger mb-1" type="button" onClick={submitInvoiceDelete}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                          <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                          <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                        </svg>
                      </span>
                      {lang.labels.delete}
                    </div>
                  </button>
                }
              </div>
            </div>
            <AppSaleViewDetail sale={"invoice"} record={invoice}></AppSaleViewDetail>
          </div>
          { (invoice as any).remote_decision === 'declined' && (invoice as any).remote_decline_reason &&
            <div className="card card-flush mb-5">
              <div className="card-body py-4">
                <div className="d-flex align-items-start">
                  <span className="badge badge-light-danger text-uppercase fs-8 me-3">{lang.labels.declineReason}</span>
                  <span className="fw-bold text-gray-800">{(invoice as any).remote_decline_reason}</span>
                </div>
              </div>
            </div>
          }
          <AppSaleViewRequirements record={invoice}></AppSaleViewRequirements>
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h4>{lang.labels.payments}</h4>
              </div>
              <div className="card-toolbar">
                { invoice.validated && invoice.status !== "completed" && invoice.status !== "charged-off" && invoice.payment_status !== "fully-paid" &&
                  <button className="btn btn-sm btn-primary mb-1 me-2" type="button" onClick={executeShowModalPaymentCreateInvoice}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-3 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M3.20001 5.91897L16.9 3.01895C17.4 2.91895 18 3.219 18.1 3.819L19.2 9.01895L3.20001 5.91897Z" fill="black"/>
                          <path opacity="0.3" d="M13 13.9189C13 12.2189 14.3 10.9189 16 10.9189H21C21.6 10.9189 22 11.3189 22 11.9189V15.9189C22 16.5189 21.6 16.9189 21 16.9189H16C14.3 16.9189 13 15.6189 13 13.9189ZM16 12.4189C15.2 12.4189 14.5 13.1189 14.5 13.9189C14.5 14.7189 15.2 15.4189 16 15.4189C16.8 15.4189 17.5 14.7189 17.5 13.9189C17.5 13.1189 16.8 12.4189 16 12.4189Z" fill="black"/>
                          <path d="M13 13.9189C13 12.2189 14.3 10.9189 16 10.9189H21V7.91895C21 6.81895 20.1 5.91895 19 5.91895H3C2.4 5.91895 2 6.31895 2 6.91895V20.9189C2 21.5189 2.4 21.9189 3 21.9189H19C20.1 21.9189 21 21.0189 21 19.9189V16.9189H16C14.3 16.9189 13 15.6189 13 13.9189Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.makePayment}
                    </div>
                  </button>
                }
                { invoice.validated && invoice.status !== "completed" && invoice.status !== "charged-off" && invoice.payment_status !== "fully-paid" && Number(invoice.amount?.debt ?? 0) > 0 &&
                  <button className="btn btn-sm btn-light-danger mb-1 me-2" type="button" onClick={executeShowModalWriteOff}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-3 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black"/>
                          <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black"/>
                          <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.writeOff}
                    </div>
                  </button>
                }
                { invoice.validated && (invoice.payments ?? []).filter((p: any) => !p.is_deleted).length > 0 &&
                  <button className="btn btn-sm btn-primary mb-1" type="button" onClick={excuteViewOrHideButtonsToPaymentDelete}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-3 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M8.38 22H21C21.2652 22 21.5196 21.8947 21.7071 21.7072C21.8946 21.5196 22 21.2652 22 21C22 20.7348 21.8946 20.4804 21.7071 20.2928C21.5196 20.1053 21.2652 20 21 20H10L8.38 22Z" fill="black"/>
                          <path d="M15.622 15.6219L9.855 21.3879C9.66117 21.582 9.43101 21.7359 9.17766 21.8409C8.92431 21.946 8.65275 22 8.37849 22C8.10424 22 7.83268 21.946 7.57933 21.8409C7.32598 21.7359 7.09582 21.582 6.90199 21.3879L2.612 17.098C2.41797 16.9042 2.26404 16.674 2.15903 16.4207C2.05401 16.1673 1.99997 15.8957 1.99997 15.6215C1.99997 15.3472 2.05401 15.0757 2.15903 14.8224C2.26404 14.569 2.41797 14.3388 2.612 14.145L8.37801 8.37805L15.622 15.6219Z" fill="black"/>
                          <path opacity="0.3" d="M8.37801 8.37805L14.145 2.61206C14.3388 2.41803 14.569 2.26408 14.8223 2.15906C15.0757 2.05404 15.3473 2 15.6215 2C15.8958 2 16.1673 2.05404 16.4207 2.15906C16.674 2.26408 16.9042 2.41803 17.098 2.61206L21.388 6.90198C21.582 7.0958 21.736 7.326 21.841 7.57935C21.946 7.83269 22 8.10429 22 8.37854C22 8.65279 21.946 8.92426 21.841 9.17761C21.736 9.43096 21.582 9.66116 21.388 9.85498L15.622 15.6219L8.37801 8.37805Z" fill="black"/>
                        </svg>
                      </span>
                      {removePayment ? lang.labels.doNotDeletePayments : lang.labels.deleteSomePayment}
                    </div>
                  </button>
                }
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="dataTables_wrapper">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed gy-2 mb-2">
                    <thead className="border-bottom">
                      <tr className="text-muted text-uppercase fw-bolder fs-7">
                        <th className="w-25px text-nowrap text-start">{lang.labels.n}</th>
                        <th className="min-w-100px text-nowrap text-start">{lang.labels.createdDate}</th>
                        <th className="min-w-100px text-nowrap text-start d-none">{lang.labels.method}</th>
                        <th className="min-w-100px text-nowrap text-start">{lang.labels.sourceOfIncome}</th>
                        <th className="min-w-100px text-nowrap text-end">{lang.labels.amount} ({invoice.currency?.code ?? ''})</th>
                        { invoice.validated && removePayment &&
                          <th className="w-75px text-nowrap text-end">{lang.labels.remove}</th>
                        }
                      </tr>
                    </thead>
                    { (invoice.payments ?? []).filter((p: any) => !p.is_deleted).length > 0
                      ?
                      <tbody className="text-secondary fw-bold">
                        { (invoice.payments ?? []).filter((p: any) => !p.is_deleted).map (( (item, index) => { return (
                          <tr key={index} className="row-dashed">
                            <td>{index + 1}</td>
                            <td>{dateFormat(lang.code, item.payment_date)}</td>
                            <td className="d-none">{paymentMethodValue(lang, item.method)}</td>
                            <td>{(item.method === 'credit-note') ? lang.labels.creditNote : treasuryAccountSelected(treasuryAccountsForCompany, item.source)}</td>
                            <td className="text-nowrap text-end">{formatMoney(item.amount)}</td>
                            { invoice.validated && removePayment &&
                              <td valign="middle" className="text-end p-0">
                                <button className="btn btn-sm btn-icon btn-flex btn-bg-transparent btn-active-color-danger h-30px w-30px" type="button" onClick={() => submitPaymentDelete(item.id)}>
                                  <span className="svg-icon svg-icon-primary svg-icon-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                                      <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                                      <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                                    </svg>
                                  </span>
                                </button>
                              </td>
                              }
                          </tr>
                        )}))}
                      </tbody>
                      :
                      <tbody>
                        <tr className="text-secondary fw-bold">
                          <td colSpan={4} className="text-center">{lang.labels.thisInvoiceHasNoPayments}</td>
                        </tr>
                      </tbody>
                    }
                  </table>
                </div>
              </div>
            </div>
          </div>
          { (invoice.write_offs ?? []).filter((w: any) => !w.is_deleted).length > 0 &&
            <div className="card card-flush mb-5">
              <div className="card-header">
                <div className="card-title">
                  <h4>{lang.labels.writeOffs}</h4>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="dataTables_wrapper">
                  <div className="table-responsive">
                    <table className="table align-middle table-row-dashed gy-2 mb-2">
                      <thead className="border-bottom">
                        <tr className="text-muted text-uppercase fw-bolder fs-7">
                          <th className="w-25px text-nowrap text-start">{lang.labels.n}</th>
                          <th className="min-w-100px text-nowrap text-start">{lang.labels.date}</th>
                          <th className="min-w-100px text-nowrap text-start">{lang.labels.writeOffLossAccount}</th>
                          <th className="min-w-200px text-nowrap text-start">{lang.labels.writeOffReason}</th>
                          <th className="min-w-100px text-nowrap text-end">{lang.labels.amount} ({invoice.currency?.code ?? ''})</th>
                          <th className="w-75px text-nowrap text-end">{lang.labels.remove}</th>
                        </tr>
                      </thead>
                      <tbody className="text-secondary fw-bold">
                        { (invoice.write_offs ?? []).filter((w: any) => !w.is_deleted).map((item: any, index: number) => (
                          <tr key={index} className="row-dashed">
                            <td>{index + 1}</td>
                            <td>{dateFormat(lang.code, item.date)}</td>
                            <td>{item.loss_account ?? '3805'}</td>
                            <td className="text-justify" style={{ whiteSpace: 'pre-line' }}>{item.reason ?? ''}</td>
                            <td className="text-nowrap text-end">{formatMoney(item.amount)}</td>
                            <td valign="middle" className="text-end p-0">
                              <button className="btn btn-sm btn-icon btn-flex btn-bg-transparent btn-active-color-danger h-30px w-30px" type="button" onClick={() => submitWriteOffDelete(item._id ?? item.id)}>
                                <span className="svg-icon svg-icon-primary svg-icon-3">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                                    <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                                    <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                                  </svg>
                                </span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          }
          { (invoice.reminders ?? []).length > 0 &&
            <div className="card card-flush mb-5">
              <div className="card-header">
                <div className="card-title">
                  <h4>{lang.labels.reminders}</h4>
                </div>
                <div className="card-toolbar">
                  { invoice.validated &&
                    <button className="btn btn-sm btn-primary mb-1 me-2" type="button" onClick={executeShowModalInvoiceCreateReminder}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-3 ms-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path opacity="0.3" d="M14 3V20H2V3C2 2.4 2.4 2 3 2H13C13.6 2 14 2.4 14 3ZM11 13V11C11 9.7 10.2 8.59995 9 8.19995V7C9 6.4 8.6 6 8 6C7.4 6 7 6.4 7 7V8.19995C5.8 8.59995 5 9.7 5 11V13C5 13.6 4.6 14 4 14V15C4 15.6 4.4 16 5 16H11C11.6 16 12 15.6 12 15V14C11.4 14 11 13.6 11 13Z" fill="black"/>
                            <path d="M2 20H14V21C14 21.6 13.6 22 13 22H3C2.4 22 2 21.6 2 21V20ZM9 3V2H7V3C7 3.6 7.4 4 8 4C8.6 4 9 3.6 9 3ZM6.5 16C6.5 16.8 7.2 17.5 8 17.5C8.8 17.5 9.5 16.8 9.5 16H6.5ZM21.7 12C21.7 11.4 21.3 11 20.7 11H17.6C17 11 16.6 11.4 16.6 12C16.6 12.6 17 13 17.6 13H20.7C21.2 13 21.7 12.6 21.7 12ZM17 8C16.6 8 16.2 7.80002 16.1 7.40002C15.9 6.90002 16.1 6.29998 16.6 6.09998L19.1 5C19.6 4.8 20.2 5 20.4 5.5C20.6 6 20.4 6.60005 19.9 6.80005L17.4 7.90002C17.3 8.00002 17.1 8 17 8ZM19.5 19.1C19.4 19.1 19.2 19.1 19.1 19L16.6 17.9C16.1 17.7 15.9 17.1 16.1 16.6C16.3 16.1 16.9 15.9 17.4 16.1L19.9 17.2C20.4 17.4 20.6 18 20.4 18.5C20.2 18.9 19.9 19.1 19.5 19.1Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.createReminder}
                      </div>
                    </button>
                  }
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="dataTables_wrapper">
                  <div className="table-responsive">
                    <table className="table align-middle table-row-dashed gy-2 mb-2">
                      <thead className="border-bottom">
                        <tr className="text-muted text-uppercase fw-bolder fs-7">
                          <th className="w-25px text-nowrap text-start">{lang.labels.n}</th>
                          <th className="min-w-100px text-nowrap text-start">{lang.labels.createdAt}</th>
                          <th className="min-w-100px text-nowrap text-start">{lang.labels.updatedAt}</th>
                          <th className="min-w-300px text-nowrap text-start">{lang.labels.description}</th>
                          <th className="min-w-100px text-nowrap text-end">{lang.labels.amount} ({invoice.currency?.code ?? ''})</th>
                          { invoice.validated &&
                            <th className="w-75px text-nowrap text-end">{lang.labels.actions}</th>
                          }
                          </tr>
                      </thead>
                      <tbody className="text-secondary fw-bold">
                        { (invoice.reminders ?? []).map (( (item, index) => { return (
                          <tr key={index} className="row-dashed">
                            <td>{index + 1}</td>
                            <td>{dateFormat(lang.code, item.created_at)}</td>
                            <td>{dateFormat(lang.code, item.updated_at)}</td>
                            <td className="text-justify" style={{ whiteSpace: 'pre-line' }}>{item.description}</td>
                            <td className="text-nowrap text-end">{formatMoney(item.amount)}</td>
                            { invoice.validated &&
                              <td valign="middle" className="text-end p-0">
                                <button className="btn btn-sm btn-icon btn-flex btn-bg-transparent btn-active-color-info h-30px w-30px" type="button" onClick={() => executeShowModalInvoiceUpdateReminder(item)}>
                                  <span className="svg-icon svg-icon-primary svg-icon-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <path opacity="0.3" d="M21.4 8.35303L19.241 10.511L13.485 4.755L15.643 2.59595C16.0248 2.21423 16.5426 1.99988 17.0825 1.99988C17.6224 1.99988 18.1402 2.21423 18.522 2.59595L21.4 5.474C21.7817 5.85581 21.9962 6.37355 21.9962 6.91345C21.9962 7.45335 21.7817 7.97122 21.4 8.35303ZM3.68699 21.932L9.88699 19.865L4.13099 14.109L2.06399 20.309C1.98815 20.5354 1.97703 20.7787 2.03189 21.0111C2.08674 21.2436 2.2054 21.4561 2.37449 21.6248C2.54359 21.7934 2.75641 21.9115 2.989 21.9658C3.22158 22.0201 3.4647 22.0084 3.69099 21.932H3.68699Z" fill="black" />
                                      <path d="M5.574 21.3L3.692 21.928C3.46591 22.0032 3.22334 22.0141 2.99144 21.9594C2.75954 21.9046 2.54744 21.7864 2.3789 21.6179C2.21036 21.4495 2.09202 21.2375 2.03711 21.0056C1.9822 20.7737 1.99289 20.5312 2.06799 20.3051L2.696 18.422L5.574 21.3ZM4.13499 14.105L9.891 19.861L19.245 10.507L13.489 4.75098L4.13499 14.105Z" fill="black" />
                                    </svg>
                                  </span>
                                </button>
                                <button className="btn btn-sm btn-icon btn-flex btn-bg-transparent btn-active-color-danger h-30px w-30px" type="button" onClick={() => submitReminderDelete(item.id)}>
                                  <span className="svg-icon svg-icon-primary svg-icon-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                      <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                                      <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                                      <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                                    </svg>
                                  </span>
                                </button>
                              </td>
                            }
                          </tr>
                        )}))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          }
          { (invoice.dunning_policy_id || invoice.dunning) &&
            <div className="card card-flush mb-5">
              <div className="card-header">
                <div className="card-title">
                  <h4>{lang.labels.dunningRelances}</h4>
                </div>
                <div className="card-toolbar">
                  { invoice.dunning?.stopped
                    ? <span className="badge badge-light-danger">{lang.labels.dunningStopped}</span>
                    : invoice.dunning?.suspended
                      ? <span className="badge badge-light-warning">{lang.labels.dunningSuspended}</span>
                      : invoice.dunning
                        ? <span className="badge badge-light-success">{lang.labels.dunningActive}</span>
                        : <span className="badge badge-light-primary">{lang.labels.dunningConfigured}</span>
                  }
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="mb-4">
                  <span className="text-muted me-2">{lang.labels.dunningPolicy}:</span>
                  <span className="fw-bold">{invoice.dunning?.policy_snapshot?.label || lang.labels.dunningConfigured}</span>
                  { invoice.dunning?.suspended && invoice.dunning?.suspended_until &&
                    <span className="text-muted ms-3">({lang.labels.dunningUntil} {new Date(invoice.dunning.suspended_until).toLocaleDateString()})</span>
                  }
                </div>
                { (invoice.dunning?.history ?? []).length > 0
                  ? <div className="table-responsive mb-4">
                      <table className="table table-row-dashed align-middle gs-0 gy-2">
                        <thead><tr className="fw-bold text-muted">
                          <th>{lang.labels.step}</th>
                          <th>{lang.labels.date}</th>
                          <th>{lang.labels.channel}</th>
                          <th className="text-end">{lang.labels.lateFee}</th>
                        </tr></thead>
                        <tbody>
                          { invoice.dunning!.history.map((h: any, i: number) => (
                            <tr key={i}>
                              <td>{h.step_index + 1}</td>
                              <td>{new Date(h.sent_at).toLocaleDateString()}</td>
                              <td>{h.channel}</td>
                              <td className="text-end">{h.late_fee_added > 0 ? h.late_fee_added.toFixed(2) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  : <div className="text-muted mb-4">{lang.labels.noDunningSent}</div>
                }
                { !invoice.dunning?.stopped &&
                  <div className="d-flex gap-2">
                    { invoice.dunning?.suspended
                      ? <button className="btn btn-sm btn-light-success" onClick={executeResumeDunning}>{lang.labels.resumeDunning}</button>
                      : <button className="btn btn-sm btn-light-warning" onClick={executeSuspendDunning}>{lang.labels.suspendDunning}</button>
                    }
                    <button className="btn btn-sm btn-light-danger" onClick={executeStopDunning}>{lang.labels.stopDunning}</button>
                  </div>
                }
              </div>
            </div>
          }
          <AppSaleViewAmount sale={"invoice"} record={invoice}></AppSaleViewAmount>
          <AppSaleViewSignatures record={invoice}></AppSaleViewSignatures>
          <AppPurchaseViewVouchers purchase={"invoice"} record={invoice} entity_type="invoice" id_entity={invoice?.id} id_company={companyForUser?.id} showVoucherActions={true} showDeleteAction={true} onVouchersChanged={() => { if (companyForUser && param.id) loadInvoice(companyForUser.id, param.id) }}></AppPurchaseViewVouchers>
          </div>
          <AppPurchaseViewDraft record={invoice} vouchers={vouchers} showGeneratedPdf={true} companyId={companyForUser?.id} pdfPath="invoices" documentId={invoice?.id}></AppPurchaseViewDraft>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalInvoiceCreateReminder id_company={companyForUser?.id ?? ''} invoice={invoice} setInvoice={setInvoiceNormalized}></AppModalInvoiceCreateReminder>
      <AppModalInvoiceUpdateReminder id_company={companyForUser?.id ?? ''} invoice={invoice} reminder={reminder} setInvoice={setInvoiceNormalized} setReminder={setReminder}></AppModalInvoiceUpdateReminder>
      <AppModalInvoiceUpdateValidate companyForUser={companyForUser} settingsForCompany={settingsForCompany} accountingAccountsForCompany={accountingAccountsForCompany} accountingIncomeAccountsForCompany={accountingIncomeAccountsForCompany} servicesForCompany={servicesForCompany} productsForCompany={productsForCompany} invoice={invoice} setInvoice={setInvoiceNormalized}></AppModalInvoiceUpdateValidate>
      <AppModalInvoiceUpdateValidate companyForUser={companyForUser} settingsForCompany={settingsForCompany} accountingAccountsForCompany={accountingAccountsForCompany} accountingIncomeAccountsForCompany={accountingIncomeAccountsForCompany} servicesForCompany={servicesForCompany} productsForCompany={productsForCompany} invoice={invoice} setInvoice={setInvoiceNormalized} mode="correction"></AppModalInvoiceUpdateValidate>
      <AppModalInvoiceUpdateInvalidate companyForUser={companyForUser} invoice={invoice} setInvoice={setInvoiceNormalized}></AppModalInvoiceUpdateInvalidate>
      <AppModalPaymentCreateInvoice companyForUser={companyForUser} settingsForCompany={settingsForCompany} accountingAccountsForCompany={accountingAccountsForCompany} treasuryAccountsForCompany={treasuryAccountsForCompany} accountingCreditEntriesForCompany={accountingCreditEntriesForCompany} invoice={invoice} setInvoice={setInvoiceNormalized} loadAccountingCreditEntriesForCompany={loadAccountingCreditEntriesForCompany}></AppModalPaymentCreateInvoice>
      <AppModalWriteOffInvoice companyForUser={companyForUser} settingsForCompany={settingsForCompany} invoice={invoice} setInvoice={setInvoiceNormalized}></AppModalWriteOffInvoice>
      { companyForUser && invoice &&
        <AppModalSendEmail companyId={companyForUser.id} documentType="invoices" documentId={invoice.id} documentNumber={invoice.invoice_number} recipientEmail={invoice.customer?.data?.email || ''} companyName={companyForUser.name} companyEmail={companyForUser.email || ''}></AppModalSendEmail>
      }
    </div>
  )
}

export default InvoiceViewPage;
